import { useRect } from '@reach/rect';
import { noop } from 'lodash';
import {
  type ComponentProps,
  type FormEventHandler,
  forwardRef,
  memo,
  type ClipboardEvent as ReactClipboardEvent,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { Mention, MentionsInput, type OnChangeHandlerFunc, type SuggestionFunc } from 'react-mentions';
import { useIsomorphicLayoutEffect } from 'react-use';

import { type User, type WorkspaceMemberType, type WorkspaceUserRolesDefinition } from '@air/api/types';

import { type CommentTimeMode } from '~/components/AnnotationsV2/hooks/useCommentTimeMode';
import { CommentActions } from '~/components/DiscussionsV2/DiscussionForm/CommentActions';
import { getRealStringPositionWithinMentions } from '~/components/DiscussionsV2/DiscussionForm/getRealStringPositionWithinMentions';
import { useDiscussionMentionSuggestions } from '~/components/DiscussionsV2/DiscussionForm/hooks/useDiscussionMentionSuggestions';
import {
  INSERT_LINK_POPOVER_ID,
  type InsertLinkButtonHandle,
} from '~/components/DiscussionsV2/DiscussionForm/InsertLinkButton';
import { TimestampButton } from '~/components/DiscussionsV2/DiscussionForm/TimestampButton';
import { inputStyles, MENTION_SUGGESTION_ID, Suggestion } from '~/components/DiscussionsV2/DiscussionForm/ui';
import { MENTION_SUGGESTIONS_CONTAINER } from '~/constants/testIDs';
import { isiOS, isSafariBrowser } from '~/utils/BrowserUtils';

type FormFields = {
  comment?: string;
  placeholder?: string;
};

export type MentionsData = {
  id: string | null;
  /** This is needed for MentionsInput */
  display: string;
  roles: WorkspaceUserRolesDefinition;
  type: WorkspaceMemberType;
} & Pick<User, 'avatar' | 'firstName' | 'lastName'>;
export type DiscussionFormProps = {
  /**
   * An array of users that will populate the mentions menu
   * when a user types the trigger `@`.
   * @see https://github.com/signavio/react-mentions
   */
  mentionsList: MentionsData[];

  /**
   * Whether the mentions data is still loading. When true, the mention trigger
   * button is disabled, and typed mention queries wait for the members to arrive.
   */
  isLoadingMentions?: boolean;

  /**
   * onCancel event that fires when the user clicks on the cancel button.
   */
  onCancel?: () => void;

  /**
   * onSubmit event that fires when the user clicks on the submit button.
   */
  onSubmit: (comment: string) => void;

  /**
   * Fires when the comment textarea gains focus.
   */
  onFocus?: () => void;

  /**
   * Determines whether or not the cancel button is visible next to the submit button,
   */
  hasCancelButton?: boolean;
  shouldShowMentionsButton?: boolean;
  hasInternalAvatar?: boolean;
  leftActions?: ReactNode;
  helperActions?: ReactNode;
  onOutsideClick?: () => void;
  'data-testid'?: string;
  // Timestamp props
  shouldEnableTimestamps?: boolean;
  setShouldEnableTimestamps?: (shouldEnableTimestamps: boolean) => void;
  formattedTimestamp?: string;
  shouldDisableTimestampsAndAnnotations?: boolean;
  timeMode?: CommentTimeMode;
  onSelectTimeMode?: (mode: CommentTimeMode) => void;
  canSelectTimeRange?: boolean;
  /** When true, blurs the textarea after a successful submit so keyboard shortcuts (e.g. spacebar) reach the video player. */
  shouldBlurOnSubmit?: boolean;
  /** When true, a draft automatically shows a Cancel button even if hasCancelButton is false. */
  shouldShowCancelOnDraft?: boolean;
} & FormFields &
  Pick<ComponentProps<'form'>, 'style'>;

export type DiscussionFormHandle = {
  focus: () => void;
};

export const DISCUSSION_FORM_ID = 'discussion-form';

const SUGGESTIONS_MAX_HEIGHT = 296;
const SUGGESTIONS_MAX_COUNT = 4;
const SUGGESTION_ITEM_HEIGHT = 52;
const MENTIONS_DROPDOWN_GAP_PX = 8;
const IOS_SCROLL_DELAY_MS = 100;
const URL_REG_EXP = /^https?:\/\/\S+$/;

const findLinkAtPlainTextPosition = (
  markupValue: string,
  cursorPos: number,
): { text: string; url: string; markupStart: number; markupEnd: number } | null => {
  const linkReg = /(@\[([^\]]+)\]\([^)]+\))|(\[([^\]]+)\]\(((?:[^()]+|\([^()]*\))+)\))/g;
  const URL_PREFIX = /^https?:\/\//;
  let plainTextPos = 0;
  let lastMarkupEnd = 0;

  for (const match of markupValue.matchAll(linkReg)) {
    if (match.index === undefined) {
      continue;
    }
    const idx = match.index;
    plainTextPos += markupValue.slice(lastMarkupEnd, idx).length;
    if (match[1] !== undefined) {
      plainTextPos += match[2].length;
    } else {
      const linkPlainEnd = plainTextPos + match[4].length;

      if (cursorPos >= plainTextPos && cursorPos < linkPlainEnd && URL_PREFIX.test(match[5])) {
        return { text: match[4], url: match[5], markupStart: idx, markupEnd: idx + match[0].length };
      }
      plainTextPos += match[4].length;
    }
    lastMarkupEnd = idx + match[0].length;
  }
  return null;
};

export const DiscussionForm = memo(
  forwardRef<DiscussionFormHandle, DiscussionFormProps>(
    (
      {
        comment,
        onCancel = noop,
        onSubmit,
        onFocus,
        placeholder,
        hasCancelButton,
        isLoadingMentions,
        mentionsList,
        shouldShowMentionsButton,
        hasInternalAvatar,
        leftActions,
        helperActions,
        onOutsideClick: onClickOutside,
        'data-testid': testId,
        style,
        // Timestamp props
        shouldEnableTimestamps,
        setShouldEnableTimestamps,
        formattedTimestamp,
        shouldDisableTimestampsAndAnnotations,
        timeMode,
        onSelectTimeMode,
        canSelectTimeRange,
        shouldBlurOnSubmit,
        shouldShowCancelOnDraft = true,
      }: DiscussionFormProps,
      ref,
    ) => {
      const [commentValue, setCommentValue] = useState(comment ?? '');
      const { getMentionSuggestions, cancelMentionSuggestions } = useDiscussionMentionSuggestions({
        mentionsList,
        isLoadingMentions: !!isLoadingMentions,
      });

      const textAreaRef = useRef<HTMLTextAreaElement>();
      const setTextAreaRef = useCallback((node: HTMLTextAreaElement | null) => {
        textAreaRef.current = node ?? undefined;
      }, []);
      const linkSelectionRef = useRef<{ start: number; end: number } | null>(null);
      const linkEditRef = useRef<{ markupStart: number; markupEnd: number } | null>(null);
      const insertLinkButtonRef = useRef<InsertLinkButtonHandle>(null);
      const isHoveringLinkRef = useRef<boolean>(false);
      const [isHoveringLink, setIsHoveringLink] = useState(false);
      const hoverRafRef = useRef<number | null>(null);
      const hostRef = useRef<HTMLDivElement | null>(null);
      const rect = useRect(textAreaRef);

      useImperativeHandle(ref, () => ({
        focus: () => {
          const textarea = textAreaRef.current;
          textarea?.focus();
          if (textarea && comment) {
            textarea.value = '';
            textarea.value = comment;
          }
        },
      }));

      const addTriggerProgrammatically = useCallback(() => {
        setCommentValue(`${commentValue}${commentValue.charAt(commentValue.length - 1) === ' ' ? '' : ' '}@`);
        textAreaRef.current?.focus();
      }, [commentValue]);

      const onEmojiPick = useCallback(
        (emoji: string) => {
          const position = textAreaRef.current?.selectionStart;
          const realStringPosition = getRealStringPositionWithinMentions(commentValue, position);
          const nextVal = commentValue.slice(0, realStringPosition) + emoji + commentValue.slice(realStringPosition);
          setCommentValue(nextVal);
          textAreaRef.current?.focus();
        },
        [commentValue],
      );

      const handleLinkInsert = useCallback(
        (text: string, url: string) => {
          const linkMarkdown = `[${text}](${url})`;
          const editRange = linkEditRef.current;
          linkEditRef.current = null;

          if (editRange !== null) {
            setCommentValue(
              commentValue.slice(0, editRange.markupStart) + linkMarkdown + commentValue.slice(editRange.markupEnd),
            );
          } else {
            const savedSelection = linkSelectionRef.current;
            linkSelectionRef.current = null;

            if (savedSelection !== null) {
              const realStart = getRealStringPositionWithinMentions(commentValue, savedSelection.start);
              const realEnd = getRealStringPositionWithinMentions(commentValue, savedSelection.end);
              setCommentValue(commentValue.slice(0, realStart) + linkMarkdown + commentValue.slice(realEnd));
            } else {
              const position = textAreaRef.current?.selectionStart;
              const realStringPosition = getRealStringPositionWithinMentions(commentValue, position);
              setCommentValue(
                commentValue.slice(0, realStringPosition) + linkMarkdown + commentValue.slice(realStringPosition),
              );
            }
          }
          textAreaRef.current?.focus();
        },
        [commentValue],
      );

      const handleMouseLeave = useCallback(() => {
        if (hoverRafRef.current !== null) {
          cancelAnimationFrame(hoverRafRef.current);
          hoverRafRef.current = null;
        }
        isHoveringLinkRef.current = false;
        setIsHoveringLink(false);
      }, []);

      const handleMouseMove = useCallback((event: ReactMouseEvent<HTMLElement>) => {
        if (hoverRafRef.current !== null) {
          return;
        }

        const { clientX, clientY } = event;
        hoverRafRef.current = requestAnimationFrame(() => {
          hoverRafRef.current = null;
          const elements = document.elementsFromPoint(clientX, clientY);
          const next = elements.some(
            (el) =>
              el instanceof HTMLElement &&
              el.tagName === 'STRONG' &&
              el.classList.contains('ads:bg-primary-subtle') &&
              el.classList.contains('bg-blue-4'),
          );

          if (next !== isHoveringLinkRef.current) {
            isHoveringLinkRef.current = next;
            setIsHoveringLink(next);
          }
        });
      }, []);

      const handleCommentClick = useCallback(
        (event: ReactMouseEvent<HTMLElement>) => {
          event.stopPropagation();
          if (event.detail !== 1) {
            return;
          }
          if (!(event.target instanceof HTMLTextAreaElement)) {
            return;
          }
          const cursorPos = event.target.selectionStart;

          if (cursorPos === null) {
            return;
          }
          const link = findLinkAtPlainTextPosition(commentValue, cursorPos);

          if (!link) {
            return;
          }
          linkEditRef.current = { markupStart: link.markupStart, markupEnd: link.markupEnd };
          linkSelectionRef.current = null;
          insertLinkButtonRef.current?.open(link.text, link.url);
        },
        [commentValue],
      );

      const getSelectedText = useCallback(() => {
        const textarea = textAreaRef.current;

        if (!textarea) {
          return '';
        }
        const { selectionStart, selectionEnd } = textarea;

        if (selectionStart === null || selectionEnd === null || selectionStart === selectionEnd) {
          linkSelectionRef.current = null;
          return '';
        }

        linkSelectionRef.current = { start: selectionStart, end: selectionEnd };
        return textarea.value.slice(selectionStart, selectionEnd);
      }, []);

      const handlePaste = useCallback(
        (event: ReactClipboardEvent<HTMLElement>) => {
          const pastedText = event.clipboardData.getData('text').trim();

          if (!URL_REG_EXP.test(pastedText)) {
            return;
          }

          if (!(event.target instanceof HTMLTextAreaElement)) {
            return;
          }

          const { selectionStart, selectionEnd } = event.target;

          if (selectionStart === null || selectionEnd === null || selectionStart === selectionEnd) {
            return;
          }

          const realStart = getRealStringPositionWithinMentions(commentValue, selectionStart);
          const realEnd = getRealStringPositionWithinMentions(commentValue, selectionEnd);

          const markupSlice = commentValue.slice(realStart, realEnd);

          if (/@\[[^\]]*\]\([^)]*\)/.test(markupSlice) || /\[[^\]]+\]\(https?:\/\//.test(markupSlice)) {
            return;
          }

          event.preventDefault();
          event.nativeEvent.stopPropagation();

          const selectedText = event.target.value.slice(selectionStart, selectionEnd);
          setCommentValue(
            `${commentValue.slice(0, realStart)}[${selectedText}](${pastedText})${commentValue.slice(realEnd)}`,
          );
        },
        [commentValue],
      );

      const handleSubmit = useCallback(() => {
        if (!commentValue.trim()) {
          return;
        }

        cancelMentionSuggestions();
        onSubmit(commentValue);
        setCommentValue('');

        if (shouldBlurOnSubmit) {
          textAreaRef.current?.blur();
        }
      }, [cancelMentionSuggestions, commentValue, onSubmit, shouldBlurOnSubmit]);

      const handleFormSubmit = useCallback<FormEventHandler<HTMLFormElement>>(
        (e) => {
          e.preventDefault();
          handleSubmit();
        },
        [handleSubmit],
      );

      const handleCancel = useCallback(() => {
        cancelMentionSuggestions();
        setCommentValue('');
        onCancel();
      }, [cancelMentionSuggestions, onCancel]);

      const handleLinkCancel = useCallback(() => {
        linkEditRef.current = null;
      }, []);

      const handleInputFocus = useCallback(() => {
        // this is a hack for safari on iOS - it does not scroll input enough, so address bar covers it
        if (isSafariBrowser && isiOS()) {
          setTimeout(
            () => document.documentElement.scrollTo({ top: document.documentElement.scrollHeight }),
            IOS_SCROLL_DELAY_MS,
          );
        }

        onFocus?.();
      }, [onFocus]);

      const handleInputChange = useCallback<OnChangeHandlerFunc>((event) => {
        setCommentValue(event.target.value);
      }, []);

      const handleInputKeyDown = useCallback(
        (event: ReactKeyboardEvent<HTMLTextAreaElement> | ReactKeyboardEvent<HTMLInputElement>) => {
          if (event.key === 'Escape') {
            event.preventDefault();
            cancelMentionSuggestions();
            setCommentValue('');
          }

          if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();

            return handleSubmit();
          }
        },
        [cancelMentionSuggestions, handleSubmit],
      );

      // react-mentions only hands back the suggestion's id/display, so look the member up again.
      const renderSuggestion = useCallback<SuggestionFunc>(
        (data) => {
          const member = mentionsList.find(({ id }) => id === data.id);

          return member ? <Suggestion {...member} /> : null;
        },
        [mentionsList],
      );

      useIsomorphicLayoutEffect(() => {
        const host = document.createElement('div');
        hostRef.current = host;
        document.body.appendChild(host);
        return () => {
          document.body.removeChild(host);
          hostRef.current = null;
        };
      }, []);

      useEffect(() => {
        const handleOutsideClick = (event: MouseEvent) => {
          const clickedInside = event.composedPath().some((el) => {
            if (!(el instanceof HTMLElement)) {
              return false;
            }

            const isEmojiPicker = el.classList.contains('emoji-mart-emoji') || el.classList.contains('emoji-mart');

            return (
              el.id === DISCUSSION_FORM_ID ||
              el.id === MENTION_SUGGESTION_ID ||
              el.id === INSERT_LINK_POPOVER_ID ||
              isEmojiPicker
            );
          });

          if (!clickedInside) {
            onClickOutside?.();
          }
        };

        if (onClickOutside) {
          window.addEventListener('click', handleOutsideClick);
        }

        return () => {
          window.removeEventListener('click', handleOutsideClick);
        };
      }, [onClickOutside]);

      useEffect(() => {
        return () => {
          if (hoverRafRef.current !== null) {
            cancelAnimationFrame(hoverRafRef.current);
          }
        };
      }, []);

      const mentionsListHeight =
        mentionsList.length > SUGGESTIONS_MAX_COUNT
          ? SUGGESTIONS_MAX_HEIGHT
          : mentionsList.length * SUGGESTION_ITEM_HEIGHT;
      const mentionsDropdownPosition =
        rect != null && mentionsList.length > 0 && rect.top >= mentionsListHeight + MENTIONS_DROPDOWN_GAP_PX
          ? {
              bottom: `${window.innerHeight - rect.top + MENTIONS_DROPDOWN_GAP_PX}px`,
              top: 'auto',
            }
          : {
              top: `${(rect?.bottom ?? 0) + MENTIONS_DROPDOWN_GAP_PX}px`,
              bottom: 'auto',
            };
      const containerStyles = {
        ...inputStyles,

        input: isHoveringLink ? { cursor: 'pointer' } : {},

        suggestions: {
          ...inputStyles.suggestions,
          zIndex: 4,
          ...mentionsDropdownPosition,
          item: {
            '&focused': {
              backgroundColor: 'var(--colors-gray-4)',
            },
          },
        },
      };

      return (
        <form
          className="flex w-full flex-col justify-between gap-2 pb-1 ads:text-secondary text-gray-11"
          onSubmit={handleFormSubmit}
          data-testid={testId}
          id={DISCUSSION_FORM_ID}
          style={style}
        >
          {shouldEnableTimestamps !== undefined && setShouldEnableTimestamps && (
            <div className="flex justify-start">
              <TimestampButton
                shouldEnableTimestamps={shouldEnableTimestamps}
                setShouldEnableTimestamps={setShouldEnableTimestamps}
                formattedTimestamp={formattedTimestamp}
                shouldDisableTimestampsAndAnnotations={shouldDisableTimestampsAndAnnotations ?? false}
                timeMode={timeMode}
                onSelectTimeMode={onSelectTimeMode}
                canSelectTimeRange={canSelectTimeRange}
              />
            </div>
          )}
          <MentionsInput
            className="mentions-input-v2 max-h-[150px] w-full overflow-x-hidden text-ellipsis rounded-4 border ads:border-input border-gray-5 ads:focus-within:ring-focus focus-within:ring-2 focus-within:ring-blue-9"
            data-testid={MENTION_SUGGESTIONS_CONTAINER}
            data-discussion-comment-textarea
            value={commentValue}
            onFocus={handleInputFocus}
            onChange={handleInputChange}
            onKeyDown={handleInputKeyDown}
            onClick={handleCommentClick}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            onPaste={handlePaste}
            placeholder={placeholder}
            inputRef={setTextAreaRef}
            style={containerStyles}
            suggestionsPortalHost={hostRef.current ?? undefined}
            allowSpaceInQuery
          >
            <Mention
              className="relative z-1 max-w-[250px] rounded-4 ads:bg-base bg-gray-1 py-0.5 ads:text-primary text-gray-12"
              trigger="@"
              data={getMentionSuggestions}
              renderSuggestion={renderSuggestion}
              appendSpaceOnAdd
            />
            {/* U+FFFE is untypeable, so react-mentions registers this Mention for chip
                styling without ever firing the suggestion dropdown */}
            <Mention
              className="rounded-4 ads:bg-primary-subtle bg-blue-4 py-0.5 font-normal ads:text-accent text-blue-11"
              markup="[__display__](__id__)"
              trigger={'￾'}
              data={[]}
            />
          </MentionsInput>
          <div className="flex items-center justify-between">
            {leftActions}
            <CommentActions
              onEmojiPick={onEmojiPick}
              onLinkInsert={handleLinkInsert}
              onLinkCancel={handleLinkCancel}
              isDisabled={!commentValue}
              isLoadingMentions={isLoadingMentions}
              addTriggerProgrammatically={addTriggerProgrammatically}
              hasCancelButton={hasCancelButton || (shouldShowCancelOnDraft && !!commentValue.trim())}
              onCancel={handleCancel}
              shouldShowMentionsButton={shouldShowMentionsButton}
              hasInternalAvatar={hasInternalAvatar}
              getSelectedText={getSelectedText}
              insertLinkButtonRef={insertLinkButtonRef}
              helperActions={helperActions}
            />
          </div>
        </form>
      );
    },
  ),
);

DiscussionForm.displayName = 'DiscussionForm';
